<script setup>
import { ref, computed } from "vue";
import { formatMoney } from "../../utils/analyticsUtils.js";

const props = defineProps({
  sales: { type: Array, default: () => [] },
  purchases: { type: Array, default: () => [] },
});

const sdPage = ref(1);
const pdPage = ref(1);
const pageSizeDet = 15;

const sdTotalPages = computed(() =>
  Math.max(1, Math.ceil(props.sales.length / pageSizeDet))
);
const pdTotalPages = computed(() =>
  Math.max(1, Math.ceil(props.purchases.length / pageSizeDet))
);

const sdPageItems = computed(() => {
  const start = (sdPage.value - 1) * pageSizeDet;
  return props.sales.slice(start, start + pageSizeDet);
});
const pdPageItems = computed(() => {
  const start = (pdPage.value - 1) * pageSizeDet;
  return props.purchases.slice(start, start + pageSizeDet);
});

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
</script>

<template>
  <section class="panel details-panel">
    <div class="panel-head">
      <div>
        <b>Ventas detalladas</b>
        <div class="panel-sub">{{ sales.length }} registros</div>
      </div>
    </div>
    <div class="table-container">
      <table class="table">
        <thead>
          <tr>
            <th>Producto</th>
            <th>Cliente</th>
            <th>Fecha</th>
            <th>Método</th>
            <th class="text-center">Cant.</th>
            <th class="text-right">Precio</th>
            <th class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in sdPageItems" :key="r.date + r.product + r.total" class="table-row">
            <td>
              <div class="product-cell">
                <div v-if="r.product_image" class="product-image">
                  <img :src="`data:${r.product_image_mime || 'image/jpeg'};base64,${r.product_image}`"
                    :alt="r.product" />
                </div>
                <div v-else class="product-image product-placeholder">
                  <span class="material-symbols-rounded">inventory_2</span>
                </div>
                <span class="product-name">{{ r.product }}</span>
              </div>
            </td>
            <td>
              <span class="customer-name">{{ r.customer || "-" }}</span>
            </td>
            <td>
              <span class="date-text">{{ formatDate(r.date) }}</span>
            </td>
            <td>
              <span class="method-badge" :class="`method-${(r.method || 'other').toLowerCase()}`">
                <span class="material-symbols-rounded method-icon">
                  {{ (r.method || '').toLowerCase() === 'cash' ? 'paid' : ((r.method || '').toLowerCase() === 'card' ?
                    'credit_card' : 'payments') }}
                </span>
                {{ r.method || "-" }}
              </span>
            </td>
            <td class="text-center">
              <span class="quantity-badge">{{ r.quantity }}</span>
            </td>
            <td class="text-right">
              <span class="price-text">{{ formatMoney(r.unit_price) }}</span>
            </td>
            <td class="text-right">
              <span class="total-text">{{ formatMoney(r.total) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="pager">
      <button class="chip" :disabled="sdPage <= 1" @click="sdPage = Math.max(1, sdPage - 1)">
        <span class="material-symbols-rounded">chevron_left</span>
        Anterior
      </button>
      <span class="muted">Página {{ sdPage }} de {{ sdTotalPages }}</span>
      <button class="chip" :disabled="sdPage >= sdTotalPages" @click="sdPage = Math.min(sdTotalPages, sdPage + 1)">
        Siguiente
        <span class="material-symbols-rounded">chevron_right</span>
      </button>
    </div>
  </section>

  <section class="panel details-panel">
    <div class="panel-head">
      <div>
        <b>Compras detalladas</b>
        <div class="panel-sub">{{ purchases.length }} registros</div>
      </div>
    </div>
    <div class="table-container">
      <table class="table">
        <thead>
          <tr>
            <th>Producto</th>
            <th>Proveedor</th>
            <th>Fecha</th>
            <th class="text-center">Cant.</th>
            <th class="text-right">Costo</th>
            <th class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in pdPageItems" :key="r.date + r.product + r.total" class="table-row">
            <td>
              <div class="product-cell">
                <div v-if="r.product_image" class="product-image">
                  <img :src="`data:${r.product_image_mime || 'image/jpeg'};base64,${r.product_image}`"
                    :alt="r.product" />
                </div>
                <div v-else class="product-image product-placeholder">
                  <span class="material-symbols-rounded">inventory_2</span>
                </div>
                <span class="product-name">{{ r.product }}</span>
              </div>
            </td>
            <td>
              <span class="customer-name">{{ r.supplier || "-" }}</span>
            </td>
            <td>
              <span class="date-text">{{ formatDate(r.date) }}</span>
            </td>
            <td class="text-center">
              <span class="quantity-badge">{{ r.quantity }}</span>
            </td>
            <td class="text-right">
              <span class="price-text">{{ formatMoney(r.unit_cost) }}</span>
            </td>
            <td class="text-right">
              <span class="total-text">{{ formatMoney(r.total) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="pager">
      <button class="chip" :disabled="pdPage <= 1" @click="pdPage = Math.max(1, pdPage - 1)">
        <span class="material-symbols-rounded">chevron_left</span>
        Anterior
      </button>
      <span class="muted">Página {{ pdPage }} de {{ pdTotalPages }}</span>
      <button class="chip" :disabled="pdPage >= pdTotalPages" @click="pdPage = Math.min(pdTotalPages, pdPage + 1)">
        Siguiente
        <span class="material-symbols-rounded">chevron_right</span>
      </button>
    </div>
  </section>
</template>

<style scoped>
.panel {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.panel-head b {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
}

.panel-sub {
  color: var(--accent);
  font-weight: 600;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.table-container {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid var(--border);
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.table thead {
  background: linear-gradient(135deg,
      rgba(124, 92, 255, 0.08) 0%,
      rgba(0, 194, 168, 0.06) 100%);
}

.table th {
  text-align: left;
  padding: 0.85rem 1rem;
  font-weight: 700;
  font-size: 0.8rem;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid var(--border);
}

.table th.text-center {
  text-align: center;
}

.table th.text-right {
  text-align: right;
}

.table tbody tr {
  transition: all 0.2s ease;
  border-bottom: 1px solid var(--border);
}

.table tbody tr:hover {
  background: rgba(124, 92, 255, 0.04);
}

.table tbody tr:last-child {
  border-bottom: none;
}

.table td {
  padding: 0.85rem 1rem;
  vertical-align: middle;
}

.table td.text-center {
  text-align: center;
}

.table td.text-right {
  text-align: right;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid var(--border);
  background: var(--surface);
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg,
      rgba(124, 92, 255, 0.1) 0%,
      rgba(0, 194, 168, 0.08) 100%);
}

.product-placeholder .material-symbols-rounded {
  font-size: 20px;
  color: var(--accent);
  opacity: 0.6;
}

.product-name {
  font-weight: 600;
  color: var(--text);
}

.customer-name {
  color: var(--text);
  font-weight: 500;
}

.date-text {
  color: var(--muted);
  font-size: 0.85rem;
}

.method-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.3rem 0.65rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.method-cash {
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
}

.method-card {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.method-transfer {
  background: rgba(168, 85, 247, 0.12);
  color: #9333ea;
}

.method-other {
  background: rgba(107, 114, 128, 0.12);
  color: #6b7280;
}

.method-icon {
  font-size: 16px;
}

.quantity-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  padding: 0.25rem 0.5rem;
  background: rgba(124, 92, 255, 0.1);
  color: var(--accent);
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.85rem;
}

.price-text {
  color: var(--muted);
  font-weight: 500;
}

.total-text {
  color: var(--text);
  font-weight: 700;
  font-size: 0.95rem;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: 1px solid var(--border);
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 8px;
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  color: var(--text);
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.2s ease;
}

.chip .material-symbols-rounded {
  font-size: 18px;
}

.chip:hover:not(:disabled) {
  background: rgba(124, 92, 255, 0.08);
  border-color: rgba(124, 92, 255, 0.3);
  transform: translateY(-1px);
}

.chip:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.muted {
  color: var(--muted);
  font-weight: 600;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .table {
    font-size: 0.85rem;
  }

  .table th,
  .table td {
    padding: 0.65rem 0.75rem;
  }

  .product-image {
    width: 32px;
    height: 32px;
  }

  .pager {
    flex-direction: column;
    gap: 0.75rem;
  }

  .chip {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .panel {
    padding: 0.85rem;
  }

  .panel-head {
    margin-bottom: 0.75rem;
  }

  .panel-head b {
    font-size: 1rem;
  }

  .panel-sub {
    font-size: 0.8rem;
  }

  .table {
    font-size: 0.8rem;
  }

  .table th,
  .table td {
    padding: 0.5rem 0.5rem;
  }

  .table th {
    font-size: 0.7rem;
  }

  .product-image {
    width: 28px;
    height: 28px;
  }

  .product-cell {
    gap: 0.5rem;
  }

  .product-name {
    font-size: 0.85rem;
  }

  .customer-name {
    font-size: 0.85rem;
  }

  .date-text {
    font-size: 0.75rem;
  }

  .method-badge {
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
    gap: 4px;
  }

  .method-icon {
    font-size: 14px;
  }

  .quantity-badge {
    min-width: 28px;
    padding: 0.2rem 0.4rem;
    font-size: 0.8rem;
  }

  .price-text,
  .total-text {
    font-size: 0.85rem;
  }

  .pager {
    padding-top: 0.75rem;
    gap: 0.5rem;
  }

  .chip {
    font-size: 0.8rem;
    padding: 0.45rem 0.75rem;
  }

  .chip .material-symbols-rounded {
    font-size: 16px;
  }

  .muted {
    font-size: 0.85rem;
  }

  /* Add horizontal scroll hint */
  .table-container::after {
    content: '← Desliza para ver más →';
    display: block;
    text-align: center;
    font-size: 0.7rem;
    color: var(--muted);
    padding: 0.5rem;
    opacity: 0.7;
  }
}
</style>
